import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import { calculateAge } from '../helpers';
import {
  DEIDENTIFIED_PATIENT_LIST_COLUMN_IDS,
  getDefaultColumns,
  resolvePatientListColumns,
} from '../components/Table/utils/columnHelper';
import { SystemPermission } from '../../context';

export const getDisplayColumns = (dashboardColumns: any, userColumns: any) => {
  // Create a map of user columns for easy lookup
  const userColumnsMap = userColumns.reduce((map: any, column: any) => {
    map[column.id] = column.enabled;
    return map;
  }, {});

  // Check each dashboard column and determine the final enabled value
  return dashboardColumns.map((dashboardColumn: any) => ({
    ...dashboardColumn,
    enabled: userColumnsMap.hasOwnProperty(dashboardColumn.id)
      ? userColumnsMap[dashboardColumn.id]
      : dashboardColumn.enabled,
  }));
};

export const useDashboardColumns = () => {
  const { selectedTenancy } = useSelector(
    (state: RootState) => state.clinicianProfile,
  );

  const dashboardColumns = selectedTenancy?.features?.dashboardColumns || [];
  const userDashboardColumns =
    selectedTenancy?.features?.userDashboardColumns || [];
  const columnsToHide = selectedTenancy?.features?.patientListHide;

  // fp_users.features > fp_clinic.features > fp_tenancy.features
  const calculateColumns = getDisplayColumns(
    dashboardColumns,
    userDashboardColumns,
  );

  const isMyPainHealthEligible = (row: any) => {
    if (!row) {
      return false;
    }
    if (typeof row.mypainhealthEligible === 'boolean') {
      return row.mypainhealthEligible;
    }
    if (typeof row.eligible === 'boolean') {
      return row.eligible;
    }
    const { years } = calculateAge(row.dob);
    return years >= 16 && years <= 24;
  };

  const hasExplicitPermission = (permission: SystemPermission) => {
    return (
      !!selectedTenancy?.permissionsSetup &&
      !!selectedTenancy?.userPermissions?.[permission]
    );
  };

  const enabledColumns = calculateColumns.filter((item: any) => item.enabled);
  const useDeidentifiedPatientListColumns = hasExplicitPermission(
    SystemPermission.DEIDENTIFIED_DATA,
  );
  const columnContext = {
    isMyPainHealthEligible,
  };

  const isUsingTenancyDashboardColumns =
    Array.isArray(dashboardColumns) &&
    dashboardColumns.length > 0 &&
    enabledColumns.length > 0;

  const getColumnConfig = () => {
    if (useDeidentifiedPatientListColumns) {
      return resolvePatientListColumns(
        DEIDENTIFIED_PATIENT_LIST_COLUMN_IDS.map((id) => ({ id })),
        columnContext,
      );
    }

    if (isUsingTenancyDashboardColumns) {
      return resolvePatientListColumns(enabledColumns, columnContext);
    }

    return getDefaultColumns(columnsToHide);
  };

  const columnConfig = getColumnConfig();

  return {
    // for updating user preference
    dashboardColumns: calculateColumns.filter((item: any) => item.editable),
    // for display columns in dashboard
    columnConfig,
    isUsingTenancyDashboardColumns,
  };
};
